"use client";

import React, {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { MdOutlineStar } from "react-icons/md";
import ButtonIcons from "@/components/ui/button-icons";
import Image from "next/image";
import { cn } from "@/lib/utils";

export interface VideoTestimonialHandle {
  playVideo: () => void;
  pauseVideo: () => void;
}

export interface VideoTestimonialProps {
  className?: string;
  quote: string;
  name: string;
  role: string;
  rating: number;
  videoURL: string;
  videoPreviewImage: string;
}

const VideoTestimonial = forwardRef<
  VideoTestimonialHandle,
  VideoTestimonialProps
>(
  (
    { className, quote, name, role, rating, videoURL, videoPreviewImage },
    ref
  ) => {
    const internalRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);

    useImperativeHandle(
      ref,
      () => ({
        playVideo() {
          if (internalRef.current) {
            internalRef.current.play();
            setIsPlaying(true);
          }
        },
        pauseVideo() {
          if (internalRef.current) {
            internalRef.current.pause();
            setIsPlaying(false);
          }
        },
      }),
      []
    );

    const handlePlay = () => {
      if (!isPlaying) {
        internalRef.current?.play();
        setIsPlaying(true);
      }
    };

    return (
      <div
        className={cn(
          "grid grid-cols-1 gap-2 md:gap-4 lg:grid-cols-2",
          className
        )}
      >
        <div
          className={cn(
            "group relative aspect-[358/200] cursor-pointer md:aspect-[696/390] lg:col-span-1 lg:aspect-[620/348]",
            isPlaying && "pointer-events-none"
          )}
          onClick={handlePlay}
          role="button"
          aria-label={isPlaying ? "Pause video" : "Play video"}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              handlePlay();
            }
          }}
        >
          <video
            ref={internalRef}
            className={cn(
              "pointer-events-none absolute inset-0 z-20 h-full w-full object-fill opacity-0 transition-all duration-500 ease-out",
              isPlaying && "pointer-events-auto opacity-100"
            )}
            controls
            controlsList="nodownload"
            aria-hidden={!isPlaying}
            aria-label="Video"
          >
            <source
              src={videoURL}
              type="video/mp4"
            />
            Your browser does not support the video tag.
          </video>
          <Image
            className={cn(
              "absolute inset-0 object-cover opacity-0 transition-all duration-500 ease-out",
              !isPlaying && "opacity-100"
            )}
            src={videoPreviewImage}
            alt="Video thumbnail"
            quality={100}
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            aria-hidden={isPlaying}
          />
          <div className="group absolute inset-0 flex cursor-pointer items-center justify-center">
            <ButtonIcons
              className={cn(
                "group-hover:bg-white group-hover:text-neutral-500",
                isPlaying && "pointer-events-none opacity-0"
              )}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handlePlay();
                }
              }}
              type={isPlaying ? "pause" : "play"}
              size="lg"
              aria-hidden="true"
            />
          </div>
        </div>
        <div className="flex !w-full select-none flex-col gap-2 lg:order-first lg:col-span-1 lg:justify-center lg:gap-4">
          <blockquote className="font-montserrat text-heading-4 text-primary-500 md:text-heading-2">
            &ldquo;{quote}&rdquo;
          </blockquote>
          <div className="flex flex-col">
            <div className="font-roboto text-body-1-400 text-neutral-500">
              {name}
            </div>
            <div className="flex flex-row items-center justify-between">
              <div className="font-roboto text-body-1-300 text-neutral-400">
                {role}
              </div>
              <div
                className="flex flex-row gap-2 lg:gap-3"
                aria-label={`Rating: ${rating} out of 5 stars`}
              >
                {Array.from({ length: 5 }).map((_, i) => (
                  <MdOutlineStar
                    key={`${name}-star-${i}`}
                    className="size-7 fill-current text-primary-400"
                    aria-label={`Star`}
                    role="img"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);
VideoTestimonial.displayName = "VideoTestimonial";

export default VideoTestimonial;
